import { rem } from 'polished'
import { useIntl, FormattedMessage } from 'react-intl'
import clsx from 'clsx'
import styled from 'styled-components'
import {
  CRUISE_SEARCH_HERO_IMAGE_ID_DESKTOP,
  CRUISE_SEARCH_HERO_IMAGE_ID_MOBILE,
  CRUISE_SEARCH_HERO_VIDEO_URL,
} from 'constants/cruise'
import { useCruiseSearchEnabled } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalCruisesSearch'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import HeroImage from 'content/components/HeroImage'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import CruiseSearchFiltersBar from '../SearchPage/Filters/CruiseSearchFiltersBar'
import CruisesTabContent from 'components/SearchV2/Components/Mobile/TabContents/CruisesTabContent'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import useCruiseInternationalMarket from 'hooks/useCruiseInternationalMarket'

const HeroContainer = styled(HeroImage)`
  min-height: unset;
  padding: ${rem(40)} 0;
  padding-bottom: ${rem(96)};
  margin-bottom: ${rem(24)};

  ${mediaQueryUp.tablet} {
    min-height: ${rem(289)};
    padding-bottom: ${rem(40)};
    margin-bottom: 0;
  }
`

const CruisesVideoContainer = styled.div`
  height: ${rem(350)};
  position: relative;

  ${mediaQueryUp.tablet} {
    height: ${rem(400)};
  }
`

const CruisesVideoBanner = styled.video`
  position: absolute;
  height: 100%;
  width: 100%;
  object-fit: cover;
`

const CruiseSearchContainerPaneContent = styled.div`
  padding: ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  box-shadow: ${props => props.theme.shadow.bottom.small};

  &.mobile-expanded {
    margin-top: ${rem(16)};
    margin-bottom: ${rem(16)};
  }
`

const CruiseSearchBarFloating = styled.div`
  position: absolute;
  bottom: 0;
  transform: translateY(50%);
  width: 100%;
  z-index: 2;

  &.floating-search-bar {
    position: relative;
    transform: unset;
    margin-top: ${rem(32)};
  }
`

const HeroCopy = styled(LayoutContainer)`
  ${mediaQueryUp.tablet} {
    transform: translateY(${rem(-16)});
  }
`

interface Props {
  floatingSearchBar?: boolean
}

function CruisePageSearchHero({ floatingSearchBar }: Props) {
  const intl = useIntl()
  const isCruiseSearchEnabled = useCruiseSearchEnabled()
  const isMobileDevice = useIsMobileDevice()
  const isCruiseInternationalMarket = useCruiseInternationalMarket()
  const heroImageId = isCruiseInternationalMarket
    ? 'nngtdap9hqhop88sevrd'
    : isMobileDevice
      ? CRUISE_SEARCH_HERO_IMAGE_ID_MOBILE
      : CRUISE_SEARCH_HERO_IMAGE_ID_DESKTOP

  const heroSubtitle = isCruiseInternationalMarket
    ? intl.formatMessage({
        id: 'nwfI4tLcZqatUuo_5Hbg',
        defaultMessage: 'Discover luxury ocean journeys with Explora Journeys.',
        description: 'Subtitle text on the international cruise landing page hero section',
      })
    : intl.formatMessage({
        id: 'PL1LjKW9RibePmwBFaDD',
        defaultMessage: 'Choose from our cruises tailored to your taste.',
        description: 'Subtitle text on the cruise landing page hero section',
      })

  const cruisesHeading = intl.formatMessage({
    id: 'l8lmZHqbvfsND1XDxcVd',
    defaultMessage: 'Cruises',
    description: 'Main heading on cruise landing page when search is disabled',
  })

  const exploreHeading = intl.formatMessage({
    id: 'agq17LcDaOTZU-mIxdhP',
    defaultMessage: 'Explore the world’s best cruises',
    description: 'Subheading on cruise landing page encouraging users to explore cruise deals',
  })

  const browseAllOffersLabel = intl.formatMessage({
    id: 'Hhe0b0hW0DGpLo5PMD5f',
    defaultMessage: 'Browse all offers',
    description: 'Button text to browse all cruise offers on landing page',
  })

  return <>
    {isCruiseSearchEnabled && <HeroContainer
      gravity="auto"
      aria-hidden="true"
      brightness={-25}
      id={heroImageId}
      tabletAspectRatio="16:3"
    >
      <HeroCopy relative>
        <Group direction="vertical" horizontalAlign="start" tabletHorizontalAlign="center" gap={8}>
          <Heading colour="dark-contrast" variant="heading2" className="hero-heading center">
            <FormattedMessage
              id="2Fgp27-eyefGI3KI91RQ"
              defaultMessage="<i>Cruise</i> into your next escape"
              description="Hero heading on cruise landing page in the cruise offer or booking section"
              values={{ i: (chunks) => <i>{chunks}</i> }}
            />
          </Heading>
          <BodyText variant="large" colour="dark-contrast" className="center">
            {heroSubtitle}
          </BodyText>
        </Group>
      </HeroCopy>

      <CruiseSearchBarFloating className={clsx({ 'floating-search-bar': floatingSearchBar })}>
        <LayoutContainer>
          <CSSBreakpoint min="tablet">
            <CruiseSearchContainerPaneContent>
              <CruiseSearchFiltersBar hideCruiseLine={isCruiseInternationalMarket} />
            </CruiseSearchContainerPaneContent>
          </CSSBreakpoint>

          <CSSBreakpoint max="mobile">
            <CruiseSearchContainerPaneContent className="mobile-expanded">
              <CruisesTabContent
                disableAutoFocus
                hideCruiseLine={isCruiseInternationalMarket}
              />
            </CruiseSearchContainerPaneContent>
          </CSSBreakpoint>
        </LayoutContainer>
      </CruiseSearchBarFloating>
    </HeroContainer>}

    {!isCruiseSearchEnabled && <CruisesVideoContainer>
      <CruisesVideoBanner
        controls={false}
        autoPlay
        playsInline
        loop
        muted
      >
        <source src={CRUISE_SEARCH_HERO_VIDEO_URL} type="video/mp4" />
      </CruisesVideoBanner>
      <Group direction="vertical" verticalAlign="center" fullHeight>
        <LayoutContainer relative>
          <Group direction="vertical" gap={24}>
            <Heading variant="heading1" colour="dark-contrast">
              {cruisesHeading}
            </Heading>
            <Heading variant="heading3alt" colour="dark-contrast">
              {exploreHeading}
            </Heading>
            <TextButton kind="secondary" variant="ghost" href="#listings">
              {browseAllOffersLabel}
            </TextButton>
          </Group>
        </LayoutContainer>
      </Group>
    </CruisesVideoContainer>}
  </>
}

export default CruisePageSearchHero
